<script lang="ts" module>
    import type { Emulator } from '#lib/languages/Emulator.ts'

    /**
     * Whether there is a built program to look at: one being debugged, or a Test's result, which
     * keeps its last Core readable. The dock's Build turns into Stop on the same condition.
     */
    export function hasBuiltProgram(emulator: Emulator) {
        return emulator.canExecute || !!emulator.compiledCode
    }
</script>

<script lang="ts">
    import Editor from '#cmp/specific/project/Editor.svelte'
    import BelowLineContent from '#cmp/specific/project/user-tools/BelowLineContent.svelte'
    import { preferencesStore } from '#stores/preferencesStore.svelte.ts'
    import { toast } from '#stores/toastStore.ts'
    import ExecutionDock, {
        type DockAction,
        type DockCompilation
    } from '#cmp/specific/project/ExecutionDock.svelte'
    import { DockSoundState } from '#cmp/specific/project/dockSound.svelte.ts'
    import { wakeAudio } from '#lib/languages/peripherals/audio/audioContext.ts'
    import { clampBigInt } from '#lib/utils.ts'
    import { terminationSummary } from '#lib/languages/termination.ts'
    import { registerColumnWidth } from '#lib/languages/registerFormats.ts'
    import { resolveProjectSettings, undoHistorySize } from '#lib/projectSettings.ts'
    import {
        displayActualSizeZoom,
        isMarsDisplay,
        programDisplay,
        rewriteProgramScreenDirective,
        screenDirectiveFile,
        type ProgramDisplay
    } from '#lib/languages/programDisplay.ts'
    import MemoryControls from '#cmp/specific/project/memory/MemoryControls.svelte'
    import MemoryVisualiser from '#cmp/specific/project/memory/MemoryRenderer.svelte'
    import { DEFAULT_MEMORY_VALUE, MEMORY_SIZE, TESTCASE_INSTRUCTION_LIMIT } from '#lib/Config.ts'
    import StatusCodesVisualiser from '#cmp/specific/project/cpu/StatusCodesRenderer.svelte'
    import RegistersVisualiser from '#cmp/specific/project/cpu/RegistersRenderer.svelte'
    import RegisterFilesPanel from '#cmp/specific/project/cpu/RegisterFilesPanel.svelte'
    import ProcessorLine from '#cmp/specific/project/cpu/ProcessorLine.svelte'
    import CyclesLine from '#cmp/specific/project/cpu/CyclesLine.svelte'
    import { onMount, type Snippet, untrack } from 'svelte'
    import { getM68kErrorMessage } from '#lib/languages/M68K/M68kUtils.ts'
    import type {
        AvailableLanguages,
        Project,
        Testcase,
        TestcaseResult
    } from '#lib/Project.svelte.ts'
    import { PlaygroundSession } from '#lib/content/PlaygroundSession.svelte.ts'
    import {
        editorFileLanguage,
        sourceLanguage,
        OPTIMIZATIONS,
        type Optimization,
        type SourceCompiler
    } from '#lib/sourceCompilation/records.ts'
    import { createProjectLanguageSessionId } from '#lib/languages/service/uri.ts'
    import { Prompt } from '#stores/promptStore.svelte.ts'
    import StdOutRenderer from '#cmp/specific/project/user-tools/StdOutRenderer.svelte'
    import TestcasesEditor from '#cmp/specific/project/testcases/TestcasesEditor.svelte'
    import { testcaseRegisters } from '#lib/testcases.ts'
    import type monaco from 'monaco-editor'
    import Card from '#cmp/shared/layout/Card.svelte'
    import {
        isLineBreakpoint,
        makeColorizedLabels,
        makeRegister,
        type RegisterPoke,
        type RegisterFormat,
        RegisterSize
    } from '#lib/languages/commonLanguageFeatures.svelte.ts'
    import ScreenRenderer from '#cmp/specific/project/screen/ScreenRenderer.svelte'
    import ScreenDisplayConfiguration from '#cmp/specific/project/screen/ScreenDisplayConfiguration.svelte'
    import type { MarsDisplayOrigin } from '#lib/languages/mars/marsDisplay.ts'
    import { languageHasScreen } from '#lib/languages/peripherals/peripheralSet.ts'
    import { X86_INPUT_INTERRUPT } from '#lib/languages/peripherals/x86Input.ts'
    import CollapsibleSection from '#cmp/specific/workbench/CollapsibleSection.svelte'
    import type { ScreenHeader } from '#cmp/specific/project/screen/screenHeader.ts'
    import '#cmp/specific/workbench/workbench.css'
    import FaListOl from '~icons/fa-solid/list-ol'
    import FaExclamationTriangle from '~icons/fa-solid/exclamation-triangle'
    import { watchViewport } from '#lib/workbench/viewport.svelte.ts'
    import { changedLinesOf, withChangedCode } from '#lib/languages/changedCode.ts'

    let running = $state(false)
    let building = $state(false)
    //a phone gets the dock as one tray across the bottom of the code, as the Workbench's
    const viewport = watchViewport()

    type Layout = 'small' | 'fullscreen'

    interface Props {
        code: string
        /** Optional transient Project: named files, a C/C++ or assembly entry, and compilation records. */
        project?: Project
        /** Selected project file, shared with a host such as the AI chat. */
        activePath?: string
        testcases?: Testcase[]
        showMemory?: boolean
        /** Whether the memory view has the region picker and tints its bytes by region. */
        showMemoryRegions?: boolean
        showConsole?: boolean
        showTestcases?: boolean
        showPc?: boolean
        showRegisters?: boolean
        showFlags?: boolean
        /**
         * Whether the cycle counter shows above the PC, on a Target that counts cycles. Off by
         * default: an example is about what its instructions do, and a Playground about timing
         * asks for it.
         */
        showCycles?: boolean
        showScreen?: boolean
        /** Whether the Screen panel starts unfolded; the small layout folds it away by default. */
        openScreen?: boolean
        /**
         * Whether the memory, the registers, the flags, the PC and the Screen wait for a built
         * program, leaving the code (and the console and Testcases) alone until then. Off by
         * default: every panel asked for is there from the start.
         */
        hideUntilBuilt?: boolean
        embedded?: boolean
        language?: AvailableLanguages
        emulator: Emulator
        children?: Snippet
        forceMemoryRight?: boolean
        layout?: Layout
        /**
         * The Register file the panel opens on, a Playground's `fpu`/`cp0`/`csr`/`sse`/`x87` flag.
         * Undefined, and an id this language has not got, open the CPU file.
         */
        initialRegisterFile?: string
        /** The initial file's reading, so a floating-point example starts with visible numbers. */
        initialRegisterFormat?: RegisterFormat
        /**
         * The caller's own controls, such as a documentation page's "Try in the editor", at the far
         * end of the execution dock floating over the bottom of the code.
         */
        dockActions?: DockAction[]
    }

    let {
        code = $bindable(),
        project,
        activePath = $bindable(undefined),
        language = 'M68K',
        showMemory: showMemoryProp,
        showMemoryRegions = false,
        showFlags: showFlagsProp,
        showRegisters: showRegistersProp,
        showConsole: showConsoleProp,
        showTestcases: showTestcasesProp,
        showPc: showPcProp,
        showCycles: showCyclesProp = false,
        showScreen: showScreenProp,
        openScreen = false,
        hideUntilBuilt = false,
        testcases = $bindable([]),
        embedded = false,
        emulator = $bindable(),
        children,
        forceMemoryRight = false,
        layout = 'small',
        initialRegisterFile = undefined,
        initialRegisterFormat = undefined,
        dockActions = []
    }: Props = $props()
    const session = untrack(() => (project ? new PlaygroundSession(project) : undefined))
    $effect(() => {
        if (session && activePath && session.project.files[activePath])
            session.selected = activePath
    })
    const modelSessionId = createProjectLanguageSessionId()
    const selectedFile = $derived(session?.selected)
    const editorLanguage = $derived(
        selectedFile ? editorFileLanguage(selectedFile, language) : language
    )
    const fileSource = $derived(
        session && selectedFile
            ? {
                  key: selectedFile,
                  value: session.project.files[selectedFile]?.content ?? '',
                  identity: {
                      sessionId: modelSessionId,
                      sourceKind: 'live' as const,
                      path: selectedFile
                  }
              }
            : undefined
    )
    const compiling = $derived(session?.compiling ?? false)
    const diagnostics = $derived([...(session?.diagnostics ?? []), ...emulator.compilerDiagnostics])
    const shownDiagnostics = $derived(
        selectedFile
            ? diagnostics.filter(
                  (diagnostic) => diagnostic.file === selectedFile || !diagnostic.file
              )
            : diagnostics
    )
    //a Playground has exactly one Program, its authored entry (programs.md, decision 12), which
    //Build builds from whichever tab is shown; a C entry has to be compiled first
    const programEntry = $derived(session?.project.selectedProgram)
    const buildDisabled = $derived(
        (session && (programEntry === undefined || !!sourceLanguage(programEntry))) ||
            emulator.compilerErrors.length > 0
    )
    const selectedIsSource = $derived(!!selectedFile && !!sourceLanguage(selectedFile))
    const displayedAssemblyFile = $derived(
        selectedFile ?? emulator.buildSources?.entry ?? emulator.entry
    )
    //what runs now where the program or a Poke changed its code goes in the hover beside the
    //machine code the Build placed, and on the line's mark
    const buildArtifacts = $derived(
        withChangedCode(
            emulator.buildArtifacts.filter((artifact) => artifact.file === displayedAssemblyFile),
            emulator.changedCode
        )
    )
    const changedLines = $derived(changedLinesOf(emulator.changedCode, displayedAssemblyFile))
    const viewZones = $derived(
        preferencesStore.values.showPseudoInstructions.value
            ? emulator.decorations
                  .filter(
                      (decoration) =>
                          (decoration.file ?? emulator.buildSources?.entry ?? emulator.entry) ===
                          displayedAssemblyFile
                  )
                  .map((decoration) => ({
                      afterLineNumber: decoration.belowLine,
                      content: BelowLineContent,
                      props: {
                          md: decoration.md,
                          note: decoration.note ?? '',
                          instructions: decoration.instructions,
                          language: editorLanguage.toLowerCase(),
                          isCurrent: (address: bigint) => emulator.pc === address
                      }
                  }))
            : []
    )
    const selectedLine = $derived.by(() => {
        if (!session || !selectedFile) return emulator.line
        if (emulator.currentFile === selectedFile) return emulator.line
        const mapped = session.project.sourceMaps[emulator.currentFile]?.lines[emulator.line]
        return mapped?.path === selectedFile ? mapped.line : -1
    })
    async function compileSourceFile() {
        if (!session || running || building) return
        try {
            await session.compile(Prompt.confirmUntilAborted)
            activePath = session.selected
        } catch (error) {
            toast.error(getM68kErrorMessage(error))
        }
    }
    const compilation = $derived<DockCompilation | undefined>(
        session?.sourcePath && (selectedIsSource || session.recompilationNeeded)
            ? {
                  label: compiling ? 'Compiling…' : selectedIsSource ? 'Compile' : 'Recompile',
                  disabled: building || running || compiling,
                  busy: compiling,
                  optimization: selectedIsSource
                      ? {
                            value: session.optimization,
                            levels: OPTIMIZATIONS,
                            onChange: (value) => {
                                session.optimization = value as Optimization
                            }
                        }
                      : undefined,
                  compiler: selectedIsSource
                      ? {
                            value: session.compiler,
                            options:
                                language === 'X86'
                                    ? [{ value: 'gcc', label: 'GCC' }]
                                    : [
                                          { value: 'clang', label: 'Clang' },
                                          { value: 'gcc', label: 'GCC' }
                                      ],
                            onChange: (value) => {
                                session.compiler = value as SourceCompiler
                            }
                        }
                      : undefined,
                  onCompile: compileSourceFile,
                  onCancel: () => session.cancel()
              }
            : undefined
    )
    const built = $derived(hasBuiltProgram(emulator))
    /** Restart's Stop and Build are under way, which keep the panels where they are. */
    let restarting = $state(false)
    //with `hideUntilBuilt` the machine's panels have nothing to say before a Build, so they leave
    //the room to the code until there is a program in them, and leave again on Stop
    const revealed = $derived(!hideUntilBuilt || built || restarting)
    let showMemory = $derived((showMemoryProp ?? true) && revealed)
    let showFlags = $derived((showFlagsProp ?? true) && revealed)
    let showRegisters = $derived((showRegistersProp ?? true) && revealed)
    //a read in a host that hides the console opens it, and it stays open for the rest of the page
    //(the plan's decision 10, docs/design/environment-library-plan.md): there is nowhere else to
    //type the answer
    let consoleRevealed = $state(false)
    let showConsole = $derived((showConsoleProp ?? layout === 'fullscreen') || consoleRevealed)
    let showTestcases = $derived(showTestcasesProp ?? false)
    let showPc = $derived((showPcProp ?? layout === 'fullscreen') && revealed)
    //a phone leaves the counter out, as the Workbench does, to keep the registers what the card is
    //about
    let showCycles = $derived(
        showCyclesProp &&
            revealed &&
            emulator.cycles !== undefined &&
            viewport.deviceClass !== 'phone'
    )
    //A shared editor only shows the Screen when its caller asks for it. Documentation playgrounds
    //derive that explicit request from their `screen` fence flag.
    let showScreen = $derived((showScreenProp ?? false) && languageHasScreen(language) && revealed)
    //no Project here, so a Playground runs on the language's default Settings
    const settings = $derived(resolveProjectSettings(language, undefined))
    //no project to save it in here, so the lecture, exam, embed and chat surfaces get the popover
    //with the display living for as long as the page does. Only MIPS, RISC-V and x86 have one
    let display = $state<ProgramDisplay>(untrack(() => programDisplay(language, undefined)))
    /** Whether the display on screen came from the program's own `@screen` comment, see `syncDisplay`. */
    let displayOrigin: MarsDisplayOrigin = $state('user')
    let displayBaseLabel: string | undefined = $state(undefined)
    const configurableDisplay = $derived(emulator.setDisplay !== undefined)

    /**
     * A Build reads the program's `@screen` directive, so the emulator may have configured itself
     * from the source; the popover follows it. A program without one changes nothing.
     */
    function syncDisplay() {
        const configured = emulator.getDisplay?.()
        if (!configured) return
        displayOrigin = configured.origin
        displayBaseLabel = configured.baseLabel
        if (configured.origin === 'directive') display = configured.display
    }
    //the small layout has no room to spare, so the Screen starts folded away in its section unless
    //the caller asked for it open: a lecture whose program draws wants the drawing visible
    let screenCollapsed = $state(untrack(() => !openScreen))
    //the section's header shows the Screen's size and controls, which the panel hands it
    let screenHeader: ScreenHeader | undefined = $state()
    let groupSize = $state(RegisterSize.Word)

    //the Playground's half of the Poke availability rule
    //([the design record](../../../../docs/design/pokes.md)): a Playground has no read-only flag,
    //so it is the Core being busy that closes the rows. The Emulator owns the other half
    const pokeable = $derived(!running && !building && emulator.canPoke)

    /** One commit of a register chunk, which is one Poke; a value too wide for it throws. */
    function pokeRegisters(fileId: string, writes: RegisterPoke[]) {
        try {
            emulator.pokeRegisters(fileId, writes)
        } catch (e) {
            toast.error(getM68kErrorMessage(e))
        }
    }

    /** One commit of a memory selection, which is one Poke however many bytes it covers. */
    function pokeMemory(address: bigint, bytes: Uint8Array) {
        try {
            emulator.pokeMemory(address, bytes)
        } catch (e) {
            toast.error(getM68kErrorMessage(e))
        }
    }
    //the fullscreen register column is pinned to the width of the CPU file, as the project page's is:
    //it sits in the same `min-content` row as the memory panel, so a column that followed the visible
    //tab would slide that panel sideways every time a tab was picked. The inline column beside the
    //editor is already a fixed 18rem and needs none of this
    let registersColumnWidth = $derived(registerColumnWidth(emulator.registerFiles, groupSize))
    //empty for a language with a single file, which has no tab to pick and goes on sizing its column
    //by what the column holds, exactly as it always did. `min-width` is set with the width because a
    //`fit-content` minimum would otherwise let the widest file win the argument anyway
    //the column is a card now, whose edges are inside its width
    let registersColumnStyle = $derived.by(() => {
        if (!registersColumnWidth) return ''
        const width = `calc(${registersColumnWidth} + 2 * var(--wb-card-inset))`
        return `width: ${width}; min-width: ${width};`
    })
    let testcasesVisible = $state(false)
    let testcasesResult: TestcaseResult[] = $state([])

    /** Test, and the Testcases window's Run all. */
    function runTests() {
        if (building || running || compiling || buildDisabled) return
        running = true
        setTimeout(async () => {
            try {
                testcasesResult = await emulator.test(
                    session ? $state.snapshot(session.sources) : $state.snapshot(code),
                    $state.snapshot(testcases),
                    TESTCASE_INSTRUCTION_LIMIT,
                    undoHistorySize(settings)
                )
            } catch (e) {
                console.error(e)
                toast.error('Error executing tests. ' + getM68kErrorMessage(e))
            } finally {
                running = false
            }
        }, 50)
    }
    let editor: monaco.editor.IStandaloneCodeEditor | undefined = $state()

    $effect(() => {
        //Tracked read outside `untrack`, so editing the playground keeps arming the live check.
        const source = session ? $state.snapshot(session.sources) : code
        //nothing to check in a C entry, which is compiled first; with none (the chat's agent
        //deleted it) the check reports why nothing builds
        if (session && programEntry !== undefined && sourceLanguage(programEntry)) return
        untrack(() =>
            typeof source === 'string' ? emulator.setCode(source) : emulator.setSources(source)
        )
        if (session)
            untrack(() => {
                if (!building && !running && !emulator.canExecute) void emulator.check()
            })
    })

    onMount(() => {
        const unregisterSourceHelp = session?.registerSourceHelp(modelSessionId)
        return () => {
            unregisterSourceHelp?.()
            session?.cancel()
            emulator.dispose()
        }
    })

    const pc = makeRegister('PC', emulator.pc, emulator.systemSize)

    $effect(() => {
        pc.setValue(emulator.pc)
        pc.setSize(emulator.systemSize)
    })

    let errorStrings = $derived(emulator.errors.join('\n'))
    //the Log's wording, without an error's message, which the console shows above it
    let info = $derived(
        emulator.terminated
            ? terminationSummary(emulator.termination, emulator.executionTime, {
                  errorMessage: false
              })
            : ''
    )
    const readWaiting = $derived(emulator.peripherals.terminal.pendingRead?.source === 'terminal')
    $effect(() => {
        if (!readWaiting) return
        untrack(() => {
            if (!emulator.peripherals.terminal.consoleAttached) consoleRevealed = true
        })
    })
    //an embed is an iframe of a fixed height, which the registers card may not outgrow: it is the
    //card that is held to what the frame leaves it, the embed's padding (`--embed-padding`, set by
    //the embed page) and the console under it, and the Register files that shrink inside it and
    //scroll, whatever the flags and the PC above them take. A minimum height on the files would win
    //over that and push the frame into scrolling
    let embeddedRegistersStyle = $derived(
        embedded
            ? `max-height: calc(var(--screen-height) - 2 * var(--embed-padding, 0.5rem) - ${showConsole ? '4.5rem' : '0px'});`
            : ''
    )

    //the small layout splits the CPU file into two name/value pairs per line, which is what fits
    //beside a 32 bit value in an 18rem column. A 64 bit language (x86, RISC-V-64) draws twice the
    //digits, so a second pair on the line would only squeeze both until their groups wrapped: those
    //languages get one pair per line and scroll instead
    let smallRegistersGridStyle = $derived(
        `grid-template-columns: ${
            Number(emulator.systemSize) > RegisterSize.Long
                ? 'min-content 1fr'
                : 'min-content 1fr min-content 1fr'
        }; gap: 0.1rem; height: 100%; justify-content: space-evenly;`
    )

    let showRegsColumn = $derived(
        showPc || showCycles || showRegisters || (showFlags && emulator.statusRegisters?.length > 0)
    )

    const sound = new DockSoundState(() => emulator.audioActivity)

    /** Sound is unlocked inside the press that starts a Build, Run or Step, as the Workbench does. */
    function wakeSound() {
        wakeAudio()
    }

    async function buildCode() {
        wakeSound()
        if (building || running || compiling || buildDisabled) return
        try {
            running = false
            building = true
            const source = session ? $state.snapshot(session.sources) : code
            if (typeof source === 'string') emulator.setCode(source)
            else emulator.setSources(source)
            await emulator.compile(undoHistorySize(settings), source)
            if (session && programEntry !== undefined) {
                session.selected = programEntry
                activePath = session.selected
            }
        } catch (e) {
            console.error(e)
            toast.error('Error compiling code. ' + getM68kErrorMessage(e))
        } finally {
            building = false
            //also after a failed build: the directive is read before the program is assembled
            syncDisplay()
        }
    }

    /**
     * The run, awaited so `running` stays true for as long as the program is in flight: that is what
     * turns the Run button into Pause here too, and what keeps Step and Undo out of a run.
     */
    async function startRun() {
        wakeSound()
        if (building || running || compiling) return
        running = true
        if (layout === 'fullscreen') {
            testcasesResult = []
        }
        try {
            //no limit: a Run is sliced, so Pause and Stop answer even in an infinite loop (ADR 0007)
            await emulator.run(0)
        } catch (e) {
            console.error(e)
            toast.error('Error executing code. ' + getM68kErrorMessage(e))
        } finally {
            running = false
        }
    }

    async function stepCode() {
        wakeSound()
        try {
            await emulator.step()
        } catch (e) {
            console.error(e)
            toast.error('Error executing code. ' + getM68kErrorMessage(e))
        }
    }

    function undoStep() {
        try {
            emulator.undo()
        } catch (e) {
            console.error(e)
            toast.error('Error executing undo ' + getM68kErrorMessage(e))
        }
    }

    function stopProgram() {
        emulator.clear()
        running = false
        if (layout === 'fullscreen') {
            testcasesResult = []
        }
    }

    /** Restart, in Run's place once the program has ended: Stop, then Build, as the two buttons do. */
    async function restartProgram() {
        if (building || running || compiling) return
        restarting = true
        try {
            stopProgram()
            await buildCode()
        } finally {
            restarting = false
        }
    }

    //an embedded playground carrying testcases is a lecture's Exercise, and the reader checks it
    //by pressing Test, so there the button stays next to the Testcases panel
    const offersTest = $derived(
        layout === 'fullscreen' || embedded
            ? testcases.length > 0
            : testcases.length > 0 && !showTestcases
    )
    //the caller's own controls, such as "Try in the editor", keep the far end of the dock, after
    //Test and the Testcases that belong with it
    const allDockActions = $derived<DockAction[]>(
        showTestcases
            ? [
                  {
                      label: 'Testcases',
                      title: 'Show the Testcases',
                      icon: testcasesResult.some((r) => !r.passed)
                          ? FaExclamationTriangle
                          : FaListOl,
                      tone: testcasesResult.some((r) => !r.passed)
                          ? 'red'
                          : testcasesResult.length > 0
                            ? 'green'
                            : undefined,
                      onClick: () => (testcasesVisible = !testcasesVisible)
                  },
                  ...dockActions
              ]
            : dockActions
    )

    function handleRegisterClick(value: bigint) {
        const clampedSize = value - (value % BigInt(emulator.memory.global.pageSize))
        emulator.setGlobalMemoryAddress(clampBigInt(clampedSize, 0n, MEMORY_SIZE[language]))
    }

    function handleEditorChange() {
        if (emulator.canExecute && emulator.terminated && emulator.line >= 0) {
            emulator.resetSelectedLine()
        }
    }
</script>

{#snippet editorSurface()}
    <div class="editor-border" class:failed={emulator.errors.length > 0}>
        {#if session}
            <div class="playground-files" role="tablist" aria-label="Program files">
                {#each Object.keys(session.project.files) as path (path)}
                    <button
                        type="button"
                        role="tab"
                        aria-selected={selectedFile === path}
                        onclick={() => {
                            session.selected = path
                            activePath = path
                        }}>{path}</button
                    >
                {/each}
            </div>
        {/if}
        <div class="playground-code">
            <Editor
                onChange={handleEditorChange}
                onBreakpointPress={(line) => emulator.toggleBreakpoint(line - 1, selectedFile)}
                onFileChange={(path, value) => session?.edit(path, value)}
                bind:editor
                bind:code
                source={fileSource}
                retainedModelKeys={session ? Object.keys(session.project.files) : undefined}
                codeOverride={session ? undefined : emulator.compiledCode}
                {buildArtifacts}
                {changedLines}
                {viewZones}
                breakpointsEditable={!selectedIsSource}
                breakpoints={emulator.breakpoints
                    .filter(isLineBreakpoint)
                    .filter((breakpoint) => breakpoint.file === displayedAssemblyFile)
                    .map((breakpoint) => breakpoint.line)}
                diagnostics={shownDiagnostics}
                language={editorLanguage}
                highlightedLine={selectedLine}
                running={running && emulator.interrupt === undefined}
                disabled={building ||
                    compiling ||
                    (emulator.canExecute && !emulator.terminated) ||
                    !!emulator.compiledCode}
                hasError={emulator.errors.length > 0}
            />
        </div>
        <div class="floating-dock">
            <ExecutionDock
                attached
                fill={viewport.deviceClass === 'phone'}
                debugging={built}
                building={building || compiling}
                {running}
                {buildDisabled}
                {compilation}
                compileOnly={selectedIsSource}
                executionDisabled={emulator.terminated || emulator.interrupt !== undefined}
                ended={emulator.terminated}
                canUndo={emulator.canUndo}
                sound={sound.value}
                hasTests={offersTest}
                actions={allDockActions}
                onBuild={buildCode}
                onStop={stopProgram}
                onRun={startRun}
                onRestart={restartProgram}
                onPause={() => emulator.pause()}
                onUndo={undoStep}
                onStep={stepCode}
                onTest={runTests}
            />
        </div>
    </div>
{/snippet}

{#snippet regsColumn(fullscreen: boolean)}
    <!-- the Workbench's registers card: the flags inset on top and ruled off from edge to edge, then
         the cycle counter and the PC, ruled off from each other, then the Register files reaching
         the card's edges -->
    <!-- in the small layout outside an embed the files ask for 15.85rem and no more, from a zero
         basis, so the row is as tall as the tallest of what sits beside them and they fill it,
         scrolling inside -->
    <div
        class="column card registers-card"
        class:data-registers-wrapper={!fullscreen}
        class:fullscreen-registers-column={fullscreen}
        style={fullscreen ? registersColumnStyle : embeddedRegistersStyle}
    >
        {#if emulator.statusRegisters?.length > 0 && showFlags}
            <div class="inset">
                <!-- a little shallower than elsewhere, for the cycle counter's room -->
                <StatusCodesVisualiser
                    statusCodes={emulator.statusRegisters}
                    style="padding: 0.1rem 0.3rem;"
                />
            </div>
            <!-- the Register files' own header sets them apart, so only the lines above them are
                 ruled off -->
            {#if showPc || showCycles}
                <div class="rule"></div>
            {/if}
        {/if}
        {#if showCycles && emulator.cycles}
            <!-- the cycle counter above the PC, when the host asks for it -->
            <CyclesLine
                cycles={emulator.cycles}
                processor={emulator.processor}
                style="padding: 0 0.9rem;"
            />
            {#if showPc}
                <div class="rule"></div>
            {/if}
        {/if}
        {#if showPc}
            <RegistersVisualiser
                systemSize={emulator.systemSize}
                {language}
                size={emulator.systemSize}
                style="flex: unset; overflow: unset; padding: 0;"
                gridStyle="padding: 0.2rem 0.7rem"
                registers={[pc]}
                withoutHeader
                position="bottom"
            />
        {/if}
        {#if showRegisters && emulator.processor}
            <!-- a program written for a later processor, `opt p=68020`, says so beside them -->
            <ProcessorLine processor={emulator.processor} style="padding: 0.1rem 0.7rem;" />
        {/if}
        {#if showRegisters}
            <RegisterFilesPanel
                systemSize={emulator.systemSize}
                {language}
                size={groupSize}
                initialFileId={initialRegisterFile}
                initialFormat={initialRegisterFormat}
                gridStyle={fullscreen ? undefined : smallRegistersGridStyle}
                style={fullscreen || embedded
                    ? 'flex: 1; min-height: 0;'
                    : 'flex: 1 1 0px; min-height: 15.85rem;'}
                files={emulator.registerFiles}
                {pokeable}
                canPokeRegister={(fileId, name) => emulator.canPokeRegister(fileId, name)}
                onPoke={pokeRegisters}
                onRegisterClick={(register) => {
                    handleRegisterClick(register.value)
                }}
            />
        {/if}
    </div>
{/snippet}

{#snippet memoryPanel(fullscreen: boolean)}
    <div class="column card memory-card" class:code-data-memory-controls={!fullscreen}>
        <div class="memory-controls">
            <MemoryControls
                {emulator}
                buttonVar="secondary"
                showRegions={showMemoryRegions}
                systemSize={emulator.systemSize}
                bytesPerPage={emulator.memory.global.pageSize}
                memorySize={MEMORY_SIZE[language]}
                currentAddress={emulator.memory.global.address}
                style={fullscreen ? undefined : 'flex: unset'}
                inputStyle={fullscreen
                    ? 'height: 100%; padding: 0 0 0 0.6rem;'
                    : 'width: 6rem; padding: 0 0 0 0.6rem;'}
                onAddressChange={(address) => emulator.setGlobalMemoryAddress(address)}
                hideLabel={!fullscreen || emulator.memory.global.rowSize < 16}
            />
        </div>
        <div class="memory-page">
            <MemoryVisualiser
                memoryRegions={showMemoryRegions ? emulator?.memoryRegions : undefined}
                dataLabels={showMemoryRegions ? emulator?.dataLabels : undefined}
                readOnlyMemory={emulator?.readOnlyMemory}
                style="{fullscreen
                    ? ''
                    : 'flex: 1; '}border-bottom-left-radius: min(var(--panel-radius, 0.5rem), 0.2rem); border-bottom-right-radius: min(var(--panel-radius, 0.5rem), 0.2rem);"
                systemSize={emulator.systemSize}
                endianess={emulator.memory.global.endianess}
                defaultMemoryValue={DEFAULT_MEMORY_VALUE[language]}
                bytesPerRow={emulator.memory.global.rowSize}
                pageSize={emulator.memory.global.pageSize}
                memory={emulator.memory.global.data}
                currentAddress={emulator.memory.global.address}
                sp={emulator.sp}
                callStackAddresses={makeColorizedLabels(emulator.callStack)}
                {pokeable}
                onPoke={pokeMemory}
            />
        </div>
    </div>
{/snippet}

{#snippet screenPanel(height: string, headerless = false)}
    <ScreenRenderer
        name={language}
        screen={emulator.peripherals.screen}
        keyboard={emulator.peripherals.keyboard}
        mouse={emulator.peripherals.mouse}
        x86Input={language === 'X86' ? emulator.peripherals.x86Input : undefined}
        waitingFor={emulator.interrupt?.type === X86_INPUT_INTERRUPT
            ? emulator.interrupt.message
            : undefined}
        actualSizeZoom={configurableDisplay ? displayActualSizeZoom(display) : 1}
        style={`height: ${height}; flex: none;${headerless ? ' border-radius: 0;' : ''}`}
        {headerless}
        onHeader={headerless ? (header) => (screenHeader = header) : undefined}
    >
        {#snippet configuration()}
            {#if configurableDisplay}
                <ScreenDisplayConfiguration
                    {display}
                    origin={displayOrigin}
                    baseLabel={displayBaseLabel}
                    onChange={(next) => {
                        //the program's own @screen comment, when it has one, is rewritten to say
                        //what was chosen, as on the project page; a program without one keeps the
                        //choice for as long as the page lives. On x86 a compiled program's comment
                        //is in its C or C++ source
                        const path =
                            session && programEntry !== undefined
                                ? screenDirectiveFile(
                                      language,
                                      session.project.files,
                                      programEntry,
                                      session.project.compilations.find(
                                          (record) => record.outputPath === programEntry
                                      )?.sourcePath
                                  )
                                : undefined
                        const source = session
                            ? path === undefined
                                ? ''
                                : (session.project.files[path]?.content ?? '')
                            : code
                        const rewritten = rewriteProgramScreenDirective(
                            language,
                            path ?? emulator.entry,
                            source,
                            display,
                            next
                        )
                        const baseChanged =
                            isMarsDisplay(next) &&
                            isMarsDisplay(display) &&
                            next.baseAddress !== display.baseAddress
                        if (rewritten !== null) {
                            if (!session) code = rewritten
                            else if (path !== undefined) session.edit(path, rewritten)
                        }
                        display = next
                        displayOrigin = rewritten !== null ? 'directive' : 'user'
                        if (rewritten === null || baseChanged) displayBaseLabel = undefined
                        emulator.setDisplay?.(next)
                    }}
                />
            {/if}
        {/snippet}
    </ScreenRenderer>
{/snippet}

{#snippet consolePanel()}
    <StdOutRenderer
        {info}
        terminal={emulator.peripherals.terminal}
        errors={errorStrings}
        {diagnostics}
        interactive={emulator.canExecute && !emulator.terminated}
        escapes={language === 'X86'}
    />
{/snippet}

{#snippet testcasesEditor()}
    {#if showTestcases}
        <TestcasesEditor
            systemSize={emulator.systemSize}
            {language}
            editable={!embedded}
            registers={testcaseRegisters(emulator)}
            bind:visible={testcasesVisible}
            {testcasesResult}
            bind:testcases
            onRun={runTests}
            onClear={() => (testcasesResult = [])}
            runDisabled={building || compiling || running || buildDisabled}
        />
    {/if}
{/snippet}

{#if layout === 'fullscreen'}
    {@render testcasesEditor()}

    <div class="playground fullscreen-editor-memory-wrapper">
        <div class="fullscreen-editor-wrapper">
            {@render editorSurface()}
        </div>
        {#if showRegsColumn || showMemory || showConsole || showScreen || children}
            <div class="fullscreen-right-side">
                {#if showRegsColumn || showMemory || children}
                    <div class="fullscreen-memory-wrapper">
                        {#if showRegsColumn}
                            {@render regsColumn(true)}
                        {/if}
                        {#if showMemory && (!children || running || built || restarting)}
                            {@render memoryPanel(true)}
                        {:else if !running && children}
                            <Card
                                background="secondary"
                                style="padding: 1rem; overflow-y: auto; width: 31.3rem; height: 33.25rem;"
                            >
                                {@render children()}
                            </Card>
                        {/if}
                    </div>
                {/if}
                {#if showScreen}
                    {@render screenPanel('26rem')}
                {/if}
                {#if showConsole}
                    {@render consolePanel()}
                {/if}
            </div>
        {/if}
    </div>
{:else}
    <div class="playground editor-wrapper">
        <div class="top-row">
            <div class="column editor">
                {@render editorSurface()}
            </div>

            {#if showRegsColumn}
                {@render regsColumn(false)}
                {#if forceMemoryRight && showMemory}
                    {@render memoryPanel(false)}
                {/if}
            {:else if showMemory}
                {@render memoryPanel(false)}
            {/if}
        </div>

        <!-- the Screen comes before the terminal, under the code that draws on it -->
        {#if showScreen}
            <CollapsibleSection
                title="Screen"
                bind:collapsed={screenCollapsed}
                info={screenCollapsed ? undefined : screenHeader?.info}
                actions={screenCollapsed ? undefined : screenHeader?.actions}
            >
                {@render screenPanel('20rem', true)}
            </CollapsibleSection>
        {/if}

        {#if showRegsColumn && showMemory && !forceMemoryRight}
            <div class="bottom-row">
                {#if showConsole}
                    {@render consolePanel()}
                {/if}
                {@render memoryPanel(false)}
            </div>
        {:else if showConsole}
            {@render consolePanel()}
        {/if}

        {@render testcasesEditor()}
    </div>
{/if}

<style lang="scss">
    /* the panels sit apart by the Workbench's gap; the small layout has no room for a Screen
       that most programs never draw on, so it folds away in a section like the debug column's,
       and the fullscreen layout shows the panel itself */
    .editor-wrapper {
        display: flex;
        flex-direction: column;
        flex: 1;
        gap: var(--wb-gap);
    }

    .top-row {
        display: flex;
        flex-wrap: wrap;
        gap: var(--wb-gap);
        flex: 1;
    }

    .bottom-row {
        display: flex;
        flex-wrap: wrap;
        gap: var(--wb-gap);
    }

    .editor {
        min-height: 15rem;
        min-width: min(100%, 25rem);
        flex: 1;
    }

    /* the Workbench's cards: the panel's surface inside a 1px edge, which clips what they hold */
    .card {
        background-color: var(--wb-surface);
        border-radius: var(--wb-radius);
        border: var(--wb-card-edge);
        overflow: hidden;
    }

    /* the Register files meet the card's edges, where a rounded corner would only show a gap */
    .registers-card {
        gap: 0.3rem;
        --panel-radius: 0px;

        .inset {
            flex: none;
            min-width: 0;
            padding: 0.2rem 0.3rem 0;
        }

        /* a border rather than a 1px fill: at a fractional screen scale a fill can round to two
           pixels, a border always to one, like the card's edges */
        .rule {
            flex: none;
            height: 0;
            border-top: 1px solid var(--wb-line);
        }
    }

    /* the address controls are ruled off from the page under them, from edge to edge of the card,
       as in the Workbench's memory card */
    .memory-card {
        min-width: 13rem;

        .memory-controls {
            display: flex;
            flex: none;
            gap: 0.4rem;
            padding: 0.3rem;
            border-bottom: 1px solid var(--wb-line);
        }

        /* the page fills the card, as tall as what sits beside it, its rows sharing the room */
        .memory-page {
            display: flex;
            flex-direction: column;
            flex: 1;
            padding: 0.3rem;
        }
    }

    /* 18rem inside the card's edges */
    .data-registers-wrapper {
        --width: calc(18rem + 2 * var(--wb-card-inset));
        max-width: var(--width);
        min-width: var(--width);
        width: var(--width);
        flex: 1;
    }

    /* and around its padding too */
    .code-data-memory-controls {
        max-width: calc(18rem + 0.6rem + 2 * var(--wb-card-inset));
    }

    @media (max-width: 720px) {
        .data-registers-wrapper {
            flex: 1;
            max-width: unset;
            width: unset;
        }
        .bottom-row {
            flex-direction: column-reverse;
        }
        .code-data-memory-controls {
            max-width: unset;
        }
    }

    /* the Workbench editor's card: the Build turning into Stop is what says the program is built,
       so there is no frame around the code while it runs, and only a program stopped on an error
       gets a red one, drawn over the card's edge */
    .editor-border {
        position: relative;
        display: flex;
        flex: 1;
        min-width: 0;
        min-height: 0;
        background-color: var(--secondary);
        border-radius: var(--wb-radius, 0.4rem);
        border: var(--wb-card-edge, 1px solid color-mix(in srgb, var(--tertiary) 60%, transparent));
        overflow: hidden;
    }

    .playground-files {
        display: flex;
        flex: none;
        overflow-x: auto;
        border-bottom: 1px solid var(--border-color);
    }

    .playground-code {
        position: relative;
        display: flex;
        flex: 1;
        min-width: 0;
        min-height: 0;
    }

    .editor-border:has(.playground-files) {
        flex-direction: column;
    }
    .playground-files button {
        flex: none;
        padding: 0.35rem 0.65rem;
        color: var(--hint);
        background: transparent;
        border: 0;
        border-bottom: 2px solid transparent;
        cursor: pointer;
    }
    .playground-files button[aria-selected='true'] {
        color: var(--primary-text);
        border-bottom-color: var(--accent);
    }
    .playground-files button:focus-visible {
        outline: 2px solid var(--accent);
        outline-offset: -2px;
    }

    /* the shadow Monaco draws under the top edge once the code is scrolled */
    .editor-border :global(.monaco-editor .scroll-decoration) {
        display: none;
    }

    /* the card's corners are the rounded ones; the editor's own would only nick them */
    .editor-border :global(.monaco-editor),
    .editor-border :global(.monaco-editor .overflow-guard) {
        border-radius: 0;
    }

    .failed::after {
        position: absolute;
        z-index: 6;
        content: '';
        inset: 0;
        border: 0.2rem solid var(--red);
        pointer-events: none;
        animation: appear 0.3s ease-in;
    }

    @keyframes appear {
        from {
            opacity: 0;
        }
        to {
            opacity: 1;
        }
    }

    /* edge to edge in the bottom corners of the code, which the card's rounding clips */
    .floating-dock {
        position: absolute;
        z-index: 5;
        left: 0;
        right: 0;
        bottom: 0;
        pointer-events: none;
    }

    .fullscreen-editor-memory-wrapper {
        display: flex;
        flex: 1;
        max-width: 100%;

        .fullscreen-editor-wrapper,
        .fullscreen-memory-wrapper {
            display: flex;
        }

        .fullscreen-editor-wrapper {
            flex-direction: column;
            flex: 1;

            @media screen and (max-width: 1000px) {
                min-height: calc(var(--screen-height) * 0.7);
            }
        }

        .fullscreen-memory-wrapper {
            gap: var(--wb-gap);
            align-items: flex-start;

            @media screen and (max-width: 1000px) {
                margin-top: 1rem;
                padding-bottom: 1rem;
                overflow-x: auto;
                width: 100%;
            }
        }

        @media screen and (max-width: 1000px) {
            flex-direction: column;
        }
    }

    .fullscreen-right-side {
        margin-left: var(--wb-gap);
        width: min-content;
        gap: var(--wb-gap);
        max-height: calc(var(--screen-height) - 4.2rem);
        padding-top: 0.2rem;
        display: flex;
        overflow-y: auto;
        flex-direction: column;
    }

    .fullscreen-registers-column {
        min-height: 0;
        height: 33.25rem;
        min-width: fit-content;
        overflow: hidden;
    }

    @media screen and (max-width: 1000px) {
        .fullscreen-right-side {
            margin: 0;
            padding: 0.2rem;
            margin-top: 1rem;
            width: unset;
            max-height: unset;
            align-items: center;
            flex-direction: column;
        }

        .fullscreen-registers-column {
            height: unset !important;
            max-height: unset !important;
            overflow: visible !important;
        }
    }
</style>
